import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  FolderOpen, 
  Package, 
  Users, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Plus,
  ArrowRight,
  Send
} from 'lucide-react';
import { format } from 'date-fns';
import GraphWidget from '../components/GraphWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/dashboard/recent-activity`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setActivity(activityRes.data.activity);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const statCards = [
    { 
      label: 'Total Documents', 
      value: stats?.total_documents || 0, 
      icon: FileText, 
      color: 'text-blue-500',
      link: '/documents'
    },
    { 
      label: 'Pending Approval', 
      value: stats?.pending_approval || 0, 
      icon: AlertCircle, 
      color: 'text-orange-500',
      link: '/documents?status=pending_approval'
    },
    { 
      label: 'Active Projects', 
      value: stats?.active_projects || 0, 
      icon: FolderOpen, 
      color: 'text-green-500',
      link: '/projects'
    },
    { 
      label: 'Total Transmittals', 
      value: stats?.total_transmittals || 0, 
      icon: Send, 
      color: 'text-indigo-500',
      link: '/transmittals'
    },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'document': return FileText;
      case 'project': return FolderOpen;
      case 'asset': return Package;
      case 'transmittal': return Send;
      default: return Clock;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-muted rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Welcome back, {user?.full_name}
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/documents">
            <Button variant="outline" size="sm" data-testid="new-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Document
            </Button>
          </Link>
          <Link to="/projects">
            <Button size="sm" data-testid="new-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>
      </div>

      <div className="p-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Link to={stat.link} key={index}>
              <Card className="stat-card hover:shadow-md transition-shadow cursor-pointer" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="stat-value">{stat.value}</p>
                      <p className="stat-label">{stat.label}</p>
                    </div>
                    <div className={`p-3 rounded-lg bg-muted ${stat.color}`}>
                      <stat.icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Recent Activity</CardTitle>
              <Link to="/notifications">
                <Button variant="ghost" size="sm">
                  View all
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activity.length > 0 ? (
                  activity.map((item, index) => {
                    const Icon = getActivityIcon(item.type);
                    return (
                      <div 
                        key={index} 
                        className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`activity-item-${index}`}
                      >
                        <div className="p-2 rounded-lg bg-muted">
                          <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.type.charAt(0).toUpperCase() + item.type.slice(1)} {item.action}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground mono">
                          {format(new Date(item.timestamp), 'MMM d, HH:mm')}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Graph Widget */}
          <GraphWidget />
        </div>

        {/* Secondary Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Stats */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Users</span>
                  <span className="text-sm font-medium mono">{stats?.total_users || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Projects</span>
                  <span className="text-sm font-medium mono">{stats?.total_projects || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Tasks</span>
                  <span className="text-sm font-medium mono">{stats?.total_tasks || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Completed Tasks</span>
                  <span className="text-sm font-medium mono">{stats?.completed_tasks || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Outgoing Transmittals</span>
                  <span className="text-sm font-medium mono">{stats?.outgoing_transmittals || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Incoming Transmittals</span>
                  <span className="text-sm font-medium mono">{stats?.incoming_transmittals || 0}</span>
                </div>
              </div>

              {stats?.total_tasks > 0 && (
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Task Completion</span>
                    <span className="text-sm font-medium">
                      {Math.round((stats?.completed_tasks / stats?.total_tasks) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 rounded-full transition-all duration-500"
                      style={{ width: `${(stats?.completed_tasks / stats?.total_tasks) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <Link to="/projects">
                  <Button variant="outline" className="w-full" size="sm">
                    <FolderOpen className="w-4 h-4 mr-2" />
                    View All Projects
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
